import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SelectFormInput, TextFormInput } from "src/components/form";
import { GroupFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { IGroupFinanceReduxFormState } from "src/reducers/reactReduxForms/groupFinance";
import * as GroupFinanceActions from "../../../actions/groupFinance";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { GroupSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./GroupFinancePage.scss";

interface GroupFinancePageProps {
    form: Dtos.GroupFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IGroupFinanceReduxFormState;
    groupFinance: Dtos.GroupFinance;
    lookups: Dtos.Lookup[];
    loadingGroupFinance: boolean;
    loadGroupFinanceSuccess: boolean;
    loadGroupFinanceFailure: boolean;
    loadGroupFinanceErrorCode: string;
    savingGroupFinance: boolean;
    saveGroupFinanceSuccess: boolean;
    saveGroupFinanceFailure: boolean;
    validatingGroupFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearGroupFinance: GroupFinanceActions.IGroupFinanceClearActionCreator;
    loadGroupFinance: GroupFinanceActions.IGroupFinanceLoadByGroupIdActionCreator;
    saveGroupFinance: GroupFinanceActions.IGroupFinanceSaveActionCreator;
    validateGroupFinance: GroupFinanceActions.IGroupFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.groupFinance";

const GroupFinancePage: FC<GroupFinancePageProps> = ({
    form,
    formState,
    formProperties,
    groupFinance,
    lookups,
    loadingGroupFinance,
    loadGroupFinanceFailure,
    loadGroupFinanceErrorCode,
    savingGroupFinance,
    saveGroupFinanceSuccess,
    saveGroupFinanceFailure,
    validatingGroupFinance,
    validationFailures,
    permissions,
    clearGroupFinance,
    loadGroupFinance,
    saveGroupFinance,
    validateGroupFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{ groupId: string }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Group Finance
    useEffect(() => {
        if (groupIdNumber && !groupFinance) {
            setBreadcrumbs(GroupFinanceBreadcrumbs(groupIdNumber));
            setDrawerItems(GroupSubMenu(groupIdNumber, permissions));

            loadGroupFinance(groupIdNumber);
        } else if (groupFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        groupIdNumber,
        groupFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadGroupFinance,
        GroupFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupFinanceFailure) {
            navigate(`/error/${loadGroupFinanceErrorCode}`);
            clearGroupFinance();
        }
    }, [
        loadGroupFinanceFailure,
        loadGroupFinanceErrorCode,
        navigate,
        clearGroupFinance,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (groupIdNumber && groupFinance?.group) {
            setBreadcrumbs(
                GroupFinanceBreadcrumbs(groupIdNumber, groupFinance.group?.name)
            );
        }
    }, [groupIdNumber, groupFinance, setBreadcrumbs, GroupFinanceBreadcrumbs]);

    // Reload group finance after saving
    useEffect(() => {
        if (!savingGroupFinance && saveGroupFinanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/groups`;
                navigate(url);
            } else if (!groupIdNumber) {
                const url = `/groups/${groupIdNumber}/finance`;
                navigate(url);
                clearGroupFinance();
            }
        }
    }, [
        savingGroupFinance,
        saveGroupFinanceSuccess,
        groupIdNumber,
        navigate,
        saveAndReturn,
        clearGroupFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingGroupFinance && saveGroupFinanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingGroupFinance, saveGroupFinanceSuccess]);

    // Cleanup effect to clear the group finance on component unmount
    useEffect(() => {
        return () => {
            clearGroupFinance();
            resetForm(reduxFormName);
        };
    }, [clearGroupFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.GroupFinance = {} as Dtos.GroupFinance;

            if (!data.groupId) {
                changes.groupId = groupIdNumber;
            }

            await saveGroupFinance(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.GroupFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/groups";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.GroupFinance = {} as Dtos.GroupFinance;

        loadForm(reduxFormName, Object.assign({ ...groupFinance }, extraData));
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.GroupFinance = {} as Dtos.GroupFinance;

            if (form && hasBeenSubmitted && !validatingGroupFinance) {
                await validateGroupFinance(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                groupIdNumber,
                null,
                permissions,
                Dtos.Permission.GroupFinanceUpdate,
                Dtos.Permission.GroupFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroupFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "groupFinance",
                                        {
                                            bctInvoicingPrefix: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "groupFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "GroupFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "groupFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "groupFinance",
                                        {
                                            fundingEmailAddress: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "groupFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingGroupFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        groupFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingGroupFinance || savingGroupFinance}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<GroupFinancePageProps> => {
    let groupFinance: Dtos.GroupFinance | undefined = !(
        state.groupFinance.formData instanceof Array
    )
        ? state.groupFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        groupFinance: groupFinance!,
        loadingGroupFinance:
            state.groupFinance.loadState &&
            state.groupFinance.loadState.status == RequestState.Pending,
        loadGroupFinanceSuccess:
            state.groupFinance.loadState &&
            state.groupFinance.loadState.status == RequestState.Success,
        loadGroupFinanceFailure:
            state.groupFinance.loadState &&
            state.groupFinance.loadState.status == RequestState.Failure,
        loadGroupFinanceErrorCode: state.groupFinance.loadState.errorCode,
        savingGroupFinance:
            state.groupFinance.saveState &&
            state.groupFinance.saveState.status == RequestState.Pending,
        saveGroupFinanceSuccess:
            state.groupFinance.saveState &&
            state.groupFinance.saveState.status == RequestState.Success,
        saveGroupFinanceFailure:
            state.groupFinance.saveState &&
            state.groupFinance.saveState.status == RequestState.Failure,
        validatingGroupFinance:
            state.groupFinance.validateState &&
            state.groupFinance.validateState.status == RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.groupFinance.lookups,
        form: state.reduxForms.groupFinance,
        formState: state.groupFinance.formState,
        formProperties: state.groupFinance.formProperties,
        reduxFormState: state.reduxForms.formState.groupFinance,
        validationFailures: groupFinance
            ? state.groupFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadGroupFinance: bindActionCreators(
            GroupFinanceActions.LoadGroupFinanceByGroupId,
            dispatch
        ),
        clearGroupFinance: bindActionCreators(
            GroupFinanceActions.Clear,
            dispatch
        ),
        saveGroupFinance: bindActionCreators(
            GroupFinanceActions.SaveGroupFinance,
            dispatch
        ),
        validateGroupFinance: bindActionCreators(
            GroupFinanceActions.ValidateGroupFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupFinancePage);
